import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { seo } from "@utils/seo";
import i18n from "@modules/shared/localization/i18n";
import { customerOrderQueryOptions } from "@modules/account/account.queryOptions";
import type { CustomerOrderQuery } from "@gql/customer/types";

export const Route = createFileRoute(
  "/$locale/_layout/(protected)/_protected/account/orders/$orderId",
)({
  component: OrderDetailPage,
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      customerOrderQueryOptions(decodeURIComponent(params.orderId)),
    ),
  head: ({ params }) => {
    const t = i18n.getFixedT(params.locale);
    const decoded = (() => {
      try {
        return decodeURIComponent(params.orderId);
      } catch {
        return params.orderId;
      }
    })();
    // Extract short id for title if it's a GID
    const shortId = decoded.includes("/") ? decoded.split("/").pop() : decoded;
    return {
      meta: [
        ...seo({
          title: t("Seo:order.title", { id: shortId }),
          description: t("Seo:order.description", { id: shortId }),
        }),
      ],
    };
  },
});

type OrderData = NonNullable<CustomerOrderQuery["order"]>;
type LineItemNode = NonNullable<
  NonNullable<OrderData["lineItems"]>["nodes"][number]
>;

function formatMoney(
  amount: string | number,
  currency: string,
  locale: string,
) {
  const value = typeof amount === "string" ? parseFloat(amount) : amount;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

function formatDate(iso: string, locale: string) {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function OrderDetailPage() {
  const { t } = useTranslation();
  const { locale, orderId } = Route.useParams();
  const decodedId = decodeURIComponent(orderId);

  const { data } = useSuspenseQuery(customerOrderQueryOptions(decodedId));
  const order = data.order;

  if (!order) {
    return (
      <div className="min-h-screen px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <Link
            to="/$locale/account/orders"
            params={{ locale }}
            className="text-text-3 hover:text-primary text-small mb-4 inline-block"
          >
            ← {t("Account.orders.backToOrders")}
          </Link>
          <p className="text-text-3 mt-8">{t("Account.orders.notFound")}</p>
        </div>
      </div>
    );
  }

  const currency = order.totalPrice?.currencyCode ?? "EUR";
  const lineItems: LineItemNode[] =
    order.lineItems?.nodes?.filter((n): n is LineItemNode => Boolean(n)) ?? [];
  const shippingAddress = order.shippingAddress;
  const fulfillment = order.fulfillments?.nodes?.[0];
  const tracking = fulfillment?.trackingInformation?.[0];

  return (
    <div className="min-h-screen px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <Link
          to="/$locale/account/orders"
          params={{ locale }}
          className="text-text-3 hover:text-primary text-small mb-4 inline-block"
        >
          ← {t("Account.orders.backToOrders")}
        </Link>

        <h1 className="text-2xlarge text-text-1 mb-2 font-bold">
          {t("Account.orders.orderNumber")}: {order.name}
        </h1>
        {order.processedAt ? (
          <p className="text-text-3 mb-8">
            {formatDate(order.processedAt, locale)}
          </p>
        ) : null}

        {/* Order Status */}
        <div className="border-border-5 mb-6 rounded-lg border p-6">
          <h2 className="text-text-1 mb-4 font-semibold">
            {t("Account.orders.status")}
          </h2>
          <div className="flex flex-wrap items-center gap-4">
            {order.financialStatus ? (
              <span className="bg-primary/10 text-primary text-small rounded-full px-3 py-1 font-medium">
                {t(`Account.orders.financialStatuses.${order.financialStatus}`)}
              </span>
            ) : null}
            {order.fulfillmentStatus ? (
              <span className="bg-primary/10 text-primary text-small rounded-full px-3 py-1 font-medium">
                {t(
                  `Account.orders.fulfillmentStatuses.${order.fulfillmentStatus}`,
                )}
              </span>
            ) : null}
          </div>
        </div>

        {/* Order Items */}
        <div className="border-border-5 mb-6 rounded-lg border p-6">
          <h2 className="text-text-1 mb-4 font-semibold">
            {t("Account.orders.orderItems")}
          </h2>
          <ul className="divide-border-5 divide-y">
            {lineItems.map((li) => (
              <li key={li.id} className="flex gap-4 py-4">
                {li.image?.url ? (
                  <img
                    src={li.image.url}
                    alt={li.image.altText ?? li.title ?? ""}
                    width={64}
                    height={64}
                    className="bg-background-2 h-16 w-16 shrink-0 rounded-lg object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    className="bg-background-2 h-16 w-16 shrink-0 rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <p className="text-text-1 font-medium">{li.title}</p>
                  <p className="text-text-3 text-small">
                    {li.variantTitle ? `${li.variantTitle} · ` : ""}
                    {t("Account.orders.items", { count: li.quantity ?? 0 })}
                  </p>
                </div>
                {li.totalPrice ? (
                  <p className="text-text-1 font-semibold">
                    {formatMoney(
                      li.totalPrice.amount,
                      li.totalPrice.currencyCode,
                      locale,
                    )}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>

        {/* Shipping Address */}
        {shippingAddress ? (
          <div className="border-border-5 mb-6 rounded-lg border p-6">
            <h2 className="text-text-1 mb-4 font-semibold">
              {t("Account.orders.shippingAddress")}
            </h2>
            <address className="text-text-3 not-italic">
              {[shippingAddress.firstName, shippingAddress.lastName]
                .filter(Boolean)
                .join(" ")}
              <br />
              {shippingAddress.address1}
              {shippingAddress.address2 ? (
                <>
                  <br />
                  {shippingAddress.address2}
                </>
              ) : null}
              <br />
              {[shippingAddress.zip, shippingAddress.city]
                .filter(Boolean)
                .join(" ")}
              {shippingAddress.territoryCode ? (
                <>
                  <br />
                  {shippingAddress.territoryCode}
                </>
              ) : null}
            </address>
          </div>
        ) : null}

        {/* Tracking */}
        {tracking && (tracking.number || tracking.url) ? (
          <div className="border-border-5 mb-6 rounded-lg border p-6">
            <h2 className="text-text-1 mb-4 font-semibold">
              {t("Account.orders.tracking")}
            </h2>
            <p className="text-text-3">
              {tracking.company ? `${tracking.company} · ` : ""}
              {tracking.url ? (
                <a
                  href={tracking.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-primary hover:underline"
                >
                  {tracking.number ?? t("Account.orders.trackingNumber")}
                </a>
              ) : (
                tracking.number
              )}
            </p>
          </div>
        ) : null}

        {/* Order Summary */}
        <div className="border-border-5 rounded-lg border p-6">
          <h2 className="text-text-1 mb-4 font-semibold">
            {t("Account.orders.orderSummary")}
          </h2>
          <div className="text-text-3 space-y-2">
            {order.subtotal ? (
              <div className="flex justify-between">
                <span>{t("Account.orders.subtotal")}</span>
                <span>
                  {formatMoney(
                    order.subtotal.amount,
                    order.subtotal.currencyCode,
                    locale,
                  )}
                </span>
              </div>
            ) : null}
            {order.totalShipping ? (
              <div className="flex justify-between">
                <span>{t("Account.orders.shipping")}</span>
                <span>
                  {formatMoney(
                    order.totalShipping.amount,
                    order.totalShipping.currencyCode,
                    locale,
                  )}
                </span>
              </div>
            ) : null}
            {order.totalTax ? (
              <div className="flex justify-between">
                <span>{t("Account.orders.tax")}</span>
                <span>
                  {formatMoney(
                    order.totalTax.amount,
                    order.totalTax.currencyCode,
                    locale,
                  )}
                </span>
              </div>
            ) : null}
            {order.totalPrice ? (
              <div className="border-border-5 text-text-1 flex justify-between border-t pt-2 font-semibold">
                <span>{t("Account.orders.total")}</span>
                <span>
                  {formatMoney(
                    order.totalPrice.amount,
                    order.totalPrice.currencyCode,
                    locale,
                  )}
                </span>
              </div>
            ) : null}
            {!order.totalPrice ? (
              <span className="sr-only">{currency}</span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
